/**
 * The people of one client as a map: who uses Numa a lot, who has stopped.
 *
 *     - distance from the centre: days active in the last 30
 *     - size:   cost in the last 30 days
 *     - colour: how long since the person was last active
 *     - badge:  cost of the last 7 days against the 7 before
 *
 * "The last 30 days" is measured back from the snapshot's own day (`asOf`),
 * never from today's date. A client whose nightly update stopped a month ago
 * was otherwise drawn with every person dormant, though they used Numa every
 * day: the map read "no update for a month" as "no use for a month". The
 * view says "as of <day>" when that day is before today.
 *
 * A person with a login who never sent a message is on the outer ring.
 * Needs the per-person data: the users tier, or the client's full snapshot.
 */
import { addDays, asOfDay, dayWords, fmtN, fmtUSD0, peopleActivityMap } from './shared';
import { ND_COLORS } from './theme';
import type { BadgeDirection, ConstellationPlanet } from './Constellation';
import type { ClientSnapshot, PerUserStats } from '@/types/fleetAnalytics';

// ─── circle size ────────────────────────────────────────────────────────

const MIN_PLANET_R = 5;
const MAX_PLANET_R = 24;

/**
 * A circle's radius for a value, against the largest value on the map. The
 * area, not the radius, grows with the value. Both maps size their circles
 * with this.
 */
export function scalePlanetRadius(value: number, max: number): number {
  if (max <= 0) return MIN_PLANET_R;
  const norm = Math.sqrt(Math.max(0, value) / max);
  return MIN_PLANET_R + norm * (MAX_PLANET_R - MIN_PLANET_R);
}

// ─── tunables ───────────────────────────────────────────────────────────

/** Active-day bucket boundaries (days active in last 30). */
const POWER_DAYS_MIN = 15; // ≥15 active days = power user (2+ per week)
const ENGAGED_DAYS_MIN = 6; // 6-14 = engaged (~weekly)
const CASUAL_DAYS_MIN = 1; // 1-5 = casual

/** 30-day cost bucket boundaries ($ per user per 30d). */
const COST_LIGHT_MAX = 5;
const COST_MODERATE_MAX = 30;
const COST_HEAVY_MAX = 100;

/** Recency bucket boundaries (days since last active). */
const RECENT_MAX_DAYS = 7; // active in last 7d
const LAPSING_MAX_DAYS = 14; // active in last 8-14d
const GOING_DARK_MAX_DAYS = 30; // active in last 15-30d
// >30d or never = dormant

/** 7-day pulse threshold (relative cost change). */
const PULSE_THRESHOLD = 0.2;

// ─── types ──────────────────────────────────────────────────────────────

export type UserEngagementBucket = 'power' | 'engaged' | 'casual' | 'dormant';
export type UserCostBucket = 'light' | 'moderate' | 'heavy' | 'very_heavy';
export type UserRecency = 'recent' | 'lapsing' | 'going_dark' | 'dormant';

export interface UserMapMetrics {
  userId: string;
  displayName: string;

  // ── Activity ─────────────────────────────────────────────────────────
  activeDays30d: number;
  lastActiveDay: string | null;
  daysSinceActive: number | null;
  recency: UserRecency;
  engagementBucket: UserEngagementBucket;

  // ── Cost ─────────────────────────────────────────────────────────────
  cost30d: number;
  cost7d: number;
  costPrior7d: number;
  costTrend7d: number;
  costBucket: UserCostBucket;
  badge: BadgeDirection;

  // ── Volume ───────────────────────────────────────────────────────────
  messages30d: number;
  convs30d: number;
}

export interface UserMapStats {
  totalUsers: number;
  activeUsers30d: number;
  dormantUsers: number;
  maxCost30d: number;
  maxActiveDays: number;
  /** Sum of all users' 30d cost — context for the detail panel. */
  totalCost30d: number;
  /** The day every "last 30 days" and "days since" on the map is measured back from. */
  asOf: string;
}

// ─── helpers ────────────────────────────────────────────────────────────

function daysBetween(isoStart: string, isoEnd: string): number {
  const a = Date.parse(isoStart + 'T00:00:00Z');
  const b = Date.parse(isoEnd + 'T00:00:00Z');
  if (isNaN(a) || isNaN(b)) return Number.POSITIVE_INFINITY;
  return Math.floor((b - a) / 86_400_000);
}

function sumDaily(daily: Record<string, number> | undefined, startDay: string, endDay: string): number {
  if (!daily) return 0;
  let s = 0;
  for (const [d, v] of Object.entries(daily)) {
    if (d >= startDay && d <= endDay) s += v || 0;
  }
  return s;
}

function countActiveDays(daily: Record<string, number> | undefined, startDay: string, endDay: string): number {
  if (!daily) return 0;
  let n = 0;
  for (const [d, v] of Object.entries(daily)) {
    if (d >= startDay && d <= endDay && (v || 0) > 0) n++;
  }
  return n;
}

function classifyEngagement(activeDays: number): UserEngagementBucket {
  if (activeDays >= POWER_DAYS_MIN) return 'power';
  if (activeDays >= ENGAGED_DAYS_MIN) return 'engaged';
  if (activeDays >= CASUAL_DAYS_MIN) return 'casual';
  return 'dormant';
}

function classifyCost(cost30d: number): UserCostBucket {
  if (cost30d <= COST_LIGHT_MAX) return 'light';
  if (cost30d <= COST_MODERATE_MAX) return 'moderate';
  if (cost30d <= COST_HEAVY_MAX) return 'heavy';
  return 'very_heavy';
}

function classifyRecency(daysSinceActive: number | null): UserRecency {
  if (daysSinceActive == null) return 'dormant';
  if (daysSinceActive <= RECENT_MAX_DAYS) return 'recent';
  if (daysSinceActive <= LAPSING_MAX_DAYS) return 'lapsing';
  if (daysSinceActive <= GOING_DARK_MAX_DAYS) return 'going_dark';
  return 'dormant';
}

function badgeFromCostTrend(recent: number, prior: number): BadgeDirection {
  if (recent === 0 && prior === 0) return null;
  if (prior === 0) return recent > PULSE_THRESHOLD ? 'up' : null;
  const ratio = (recent - prior) / prior;
  if (ratio > PULSE_THRESHOLD) return 'up';
  if (ratio < -PULSE_THRESHOLD) return 'down';
  return null;
}

function relativeCostTrend(recent: number, prior: number): number {
  if (recent === 0 && prior === 0) return 0;
  if (prior === 0) return 1;
  return (recent - prior) / prior;
}

function displayNameFor(userId: string, subToEmail: Record<string, string> | undefined): string {
  const email = subToEmail?.[userId];
  if (email) return email;
  // Messages on record and no login to match: almost always a person whose
  // login was removed after they used Numa.
  return 'No login on record';
}

/** Short label rendered on the planet itself. Email gets trimmed to the
 *  username (left of @) since full addresses are too long for the SVG.
 *  Tooltip + detail panel still show the full display name. */
function shortLabelFor(displayName: string): string {
  if (displayName.includes('@')) return displayName.split('@')[0];
  return displayName;
}

// ─── main ───────────────────────────────────────────────────────────────

/**
 * Per-person figures for one client snapshot, measured back from `asOf`
 * (the snapshot's own day unless one is passed). People with a login who
 * never sent a message are included, on the outer ring.
 *
 * The windows are whole: 30 days is the 30 days up to and including `asOf`,
 * 7 days is the 7 up to and including it, and the "7 before" are the 7 days
 * before those. They used to run 31 and 8 days, so a 7-day cost was compared
 * with an 8-day one.
 */
export function computeClientUserMap(
  snapshot: ClientSnapshot,
  asOf: string = asOfDay(snapshot)
): {
  metrics: UserMapMetrics[];
  stats: UserMapStats;
  asOf: string;
  /** True when the snapshot cannot tell a typed message from a schedule's prompt: a person whose schedule ran reads as active. */
  countsSchedules: boolean;
} {
  const today = asOf;
  const day7 = addDays(asOf, -6);
  const prior7Start = addDays(asOf, -13);
  const prior7End = addDays(asOf, -7);
  const day30 = addDays(asOf, -29);

  const byUser = snapshot.chat?.by_user || {};
  const dailyCostByUser = snapshot.chat?.daily_cost_by_user || {};
  const dailyMessagesByUser = snapshot.chat?.daily_messages_by_user || {};
  // "Active" is the person using Numa themselves: a message they typed, on
  // the days they typed one. A schedule they own that ran is not the person.
  const activity = peopleActivityMap(snapshot.chat);
  const dailyActivityByUser = activity.map || {};
  const subToEmail = snapshot.users?.sub_to_email;

  // Union of every user we know about: anyone with chat activity + anyone
  // listed in Cognito. The Cognito-only set surfaces never-active seats.
  const allUserIds = new Set<string>([
    ...Object.keys(byUser),
    ...Object.keys(dailyCostByUser),
    ...Object.keys(dailyMessagesByUser),
    ...Object.keys(subToEmail || {}),
  ]);

  const metrics: UserMapMetrics[] = [];

  for (const userId of allUserIds) {
    const stats: PerUserStats | undefined = byUser[userId];
    const dailyCost = dailyCostByUser[userId];
    const dailyMessages = dailyMessagesByUser[userId];

    const activeDays30d = countActiveDays(dailyActivityByUser[userId], day30, today);
    const cost30d = sumDaily(dailyCost, day30, today);
    const cost7d = sumDaily(dailyCost, day7, today);
    const costPrior7d = sumDaily(dailyCost, prior7Start, prior7End);
    const messages30d = sumDaily(dailyMessages, day30, today);
    const convs30d = 0; // no per-day convs map keyed by user; leave 0 for now

    const lastAt = activity.exact ? stats?.adhoc_last_at : stats?.last_at;
    const lastActiveRaw = lastAt ? lastAt.slice(0, 10) : null;
    const daysSinceActive = lastActiveRaw && lastActiveRaw <= today ? daysBetween(lastActiveRaw, today) : null;

    metrics.push({
      userId,
      displayName: displayNameFor(userId, subToEmail),
      activeDays30d,
      lastActiveDay: lastActiveRaw,
      daysSinceActive,
      recency: classifyRecency(daysSinceActive),
      engagementBucket: classifyEngagement(activeDays30d),
      cost30d,
      cost7d,
      costPrior7d,
      costTrend7d: relativeCostTrend(cost7d, costPrior7d),
      costBucket: classifyCost(cost30d),
      badge: badgeFromCostTrend(cost7d, costPrior7d),
      messages30d,
      convs30d,
    });
  }

  let maxCost = 0;
  let maxDays = 0;
  let active = 0;
  let dormant = 0;
  let totalCost = 0;
  for (const m of metrics) {
    if (m.cost30d > maxCost) maxCost = m.cost30d;
    if (m.activeDays30d > maxDays) maxDays = m.activeDays30d;
    if (m.activeDays30d > 0) active++;
    else dormant++;
    totalCost += m.cost30d;
  }

  return {
    metrics,
    stats: {
      totalUsers: metrics.length,
      activeUsers30d: active,
      dormantUsers: dormant,
      maxCost30d: maxCost,
      maxActiveDays: maxDays,
      totalCost30d: totalCost,
      asOf,
    },
    asOf,
    countsSchedules: !activity.exact && activity.map !== undefined,
  };
}

// ─── planet builders (data → Constellation render input) ───────────────

const ENGAGEMENT_RING_ORDER: UserEngagementBucket[] = ['power', 'engaged', 'casual', 'dormant'];

/** The four bands, from the centre out. The same words as the tiles beside the map. */
export function userRingLabels(): [string, string, string, string] {
  return ['Power', 'Engaged', 'Casual', 'Dormant'];
}

function userRingIndex(m: UserMapMetrics): number {
  return ENGAGEMENT_RING_ORDER.indexOf(m.engagementBucket);
}

/** A circle's colour: how long since the person was last active. */
function recencyColor(r: UserRecency): string {
  switch (r) {
    case 'recent':
      return ND_COLORS.good; // in the last 7 days
    case 'lapsing':
      return ND_COLORS.warn; // 8 to 14 days ago
    case 'going_dark':
      return ND_COLORS.bad; // 15 to 30 days ago
    case 'dormant':
    default:
      return ND_COLORS.neutral; // over 30 days ago, or never
  }
}

/** The hover card for one person. The first line is the title. */
function userTooltipLines(m: UserMapMetrics, asOf: string): string[] {
  const lines: string[] = [m.displayName];
  lines.push(`Active on ${fmtN(m.activeDays30d)} of the last 30 days`);
  if (m.daysSinceActive == null) {
    lines.push('Never sent a message');
  } else if (m.daysSinceActive === 0) {
    lines.push(`Last active ${dayWords(asOf, false)}, the last day of data`);
  } else {
    // Counted back from the last day of data, which is not always today.
    const days = m.daysSinceActive === 1 ? '1 day' : `${fmtN(m.daysSinceActive)} days`;
    lines.push(`Last active ${dayWords(m.lastActiveDay || '', false)}, ${days} before the last day of data`);
  }
  lines.push(`${fmtUSD0(m.cost30d)} in the last 30 days`);
  if (m.badge) {
    const change = `${Math.abs(m.costTrend7d * 100).toFixed(0)}%`;
    lines.push(`Cost ${m.badge === 'up' ? 'up' : 'down'} ${change} on the 7 days before`);
  }
  return lines;
}

/** Map user metrics → ConstellationPlanet[] for the single unified view. */
export function buildUserPlanets(metrics: UserMapMetrics[], stats: UserMapStats): ConstellationPlanet[] {
  return metrics.map((m) => ({
    key: m.userId,
    ringIndex: userRingIndex(m),
    fillColor: recencyColor(m.recency),
    sizeR: scalePlanetRadius(m.cost30d, stats.maxCost30d),
    badge: m.badge,
    label: shortLabelFor(m.displayName),
    tooltipLines: userTooltipLines(m, stats.asOf),
  }));
}

// ─── display tables (legend / filters) ─────────────────────────────────

export const USER_ENGAGEMENT_BUCKETS: Array<{ key: UserEngagementBucket; label: string; description: string }> = [
  { key: 'power', label: 'Power', description: `Active on ${POWER_DAYS_MIN} or more of the last 30 days` },
  {
    key: 'engaged',
    label: 'Engaged',
    description: `Active on ${ENGAGED_DAYS_MIN} to ${POWER_DAYS_MIN - 1} of the last 30 days`,
  },
  {
    key: 'casual',
    label: 'Casual',
    description: `Active on ${CASUAL_DAYS_MIN} to ${ENGAGED_DAYS_MIN - 1} of the last 30 days`,
  },
  { key: 'dormant', label: 'Dormant', description: 'Not active in the last 30 days, or never' },
];

// Money in a description goes through the currency format, read when drawn:
// the currency can change after this file loads.
export const USER_COST_BUCKETS: Array<{ key: UserCostBucket; label: string; description: string }> = [
  {
    key: 'light',
    label: 'Light',
    get description() {
      return `Up to ${fmtUSD0(COST_LIGHT_MAX)} in the last 30 days`;
    },
  },
  {
    key: 'moderate',
    label: 'Moderate',
    get description() {
      return `${fmtUSD0(COST_LIGHT_MAX)} to ${fmtUSD0(COST_MODERATE_MAX)} in the last 30 days`;
    },
  },
  {
    key: 'heavy',
    label: 'Heavy',
    get description() {
      return `${fmtUSD0(COST_MODERATE_MAX)} to ${fmtUSD0(COST_HEAVY_MAX)} in the last 30 days`;
    },
  },
  {
    key: 'very_heavy',
    label: 'Very heavy',
    get description() {
      return `Over ${fmtUSD0(COST_HEAVY_MAX)} in the last 30 days`;
    },
  },
];

export const USER_RECENCY_LABELS: Record<UserRecency, { label: string; description: string }> = {
  recent: { label: 'Recent', description: `Active in the last ${RECENT_MAX_DAYS} days` },
  lapsing: {
    label: 'Lapsing',
    description: `Last active ${RECENT_MAX_DAYS + 1} to ${LAPSING_MAX_DAYS} days ago`,
  },
  going_dark: {
    label: 'Going quiet',
    description: `Last active ${LAPSING_MAX_DAYS + 1} to ${GOING_DARK_MAX_DAYS} days ago`,
  },
  dormant: { label: 'Dormant', description: 'Not active in the last 30 days, or never' },
};
